/**
 * Blocs ajoutés au tiroir panier Horizon : carrousel d'upsell et ajout au panier.
 *
 * Deux règles tirées des erreurs passées sur le panier Nyvora :
 *
 * 1. L'ajout passe par un vrai POST /cart/add.js, puis on demande à Horizon de
 *    re-rendre son tiroir. On ne touche jamais au DOM du panier à la main : le thème
 *    recalcule les totaux, les remises et la pastille du panier tout seul.
 *
 * 2. Le tiroir est re-rendu à chaque changement, donc les écouteurs posés sur ses
 *    éléments disparaissent. On délègue tout sur `document` et on se repère par
 *    attribut — un écouteur posé une fois pour toutes, qui survit aux re-rendus.
 */

(() => {
  if (window.__mrvCartPret) return;
  window.__mrvCartPret = true;

  const SELECTEUR_PISTE = '[data-mrv-upsell-piste]';
  const URL_EVENEMENTS_SECOURS = 'https://cdn.shopify.com/storefront/standard-events.js';

  const racine = () => window.Shopify?.routes?.root || '/';

  /**
   * Charge le module d'événements standard Shopify, celui-là même qu'Horizon utilise.
   *
   * On prend d'abord l'URL déclarée dans l'importmap de la page, pour être sur la même
   * version que le thème, et on retombe sur l'URL publique si l'importmap change de forme.
   */
  let moduleEvenements = null;
  function chargerEvenements() {
    if (moduleEvenements) return moduleEvenements;
    let url = URL_EVENEMENTS_SECOURS;
    try {
      const carte = document.querySelector('script[type="importmap"]');
      const declaree = carte && JSON.parse(carte.textContent).imports?.['@shopify/events'];
      if (declaree) url = declaree;
    } catch (_) {
      /* importmap illisible : on garde l'URL de secours */
    }
    moduleEvenements = import(url);
    return moduleEvenements;
  }

  /**
   * Prévient Horizon qu'une ligne a été ajoutée, pour qu'il redessine le tiroir et la
   * pastille du panier.
   *
   * Horizon n'écoute PAS `cart:update` ni `cart:refresh` — ces deux-là n'existent nulle
   * part dans le thème, et les émettre ne faisait rien : le produit partait bien au
   * panier côté serveur, mais le tiroir restait figé. Le vrai contrat est
   * `shopify:cart:lines-update`, un événement standard Shopify qui porte une promesse
   * différée ; `component-cart-items.js` l'attend, puis re-rend sa section.
   *
   * On résout la promesse SANS clé `sections` : dans ce cas le thème va chercher la
   * section lui-même (`#refreshCartSection`). C'est un aller-retour de plus, mais ça
   * évite de dupliquer sa mécanique de rendu de sections, qui bouge d'une version à
   * l'autre du thème.
   */
  /**
   * Vrai pendant nos propres ajouts d'upsell, pour que la redirection ci-dessous les
   * ignore : un ajout depuis le panier ne doit pas recharger le panier.
   */
  let ajoutInterne = false;

  async function signalerAjout(idVariante) {
    const { CartLinesUpdateEvent } = await chargerEvenements();
    const differe = CartLinesUpdateEvent.createPromise();

    ajoutInterne = true;
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(idVariante), quantity: 1 }],
        promise: differe.promise,
      })
    );
    ajoutInterne = false;

    try {
      const panier = await fetch(`${racine()}cart.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      differe.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(panier),
        detail: {
          items: panier.items,
          source: 'mrv-upsell',
          itemCount: 1,
          didError: false,
        },
      });
    } catch (erreur) {
      differe.reject(erreur);
      throw erreur;
    }
  }

  async function ajouterAuPanier(bouton) {
    const idVariante = bouton.dataset.variante;
    if (!idVariante || bouton.getAttribute('aria-busy') === 'true') return;

    bouton.setAttribute('aria-busy', 'true');

    try {
      const reponse = await fetch(`${racine()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(idVariante), quantity: 1 }] }),
      });

      if (!reponse.ok) {
        // Un 422 veut dire rupture de stock ou variante invalide : on le laisse visible
        // dans la console plutôt que d'échouer en silence.
        console.warn('[mrv-cart] ajout refusé', reponse.status, await reponse.text());
        return;
      }

      await signalerAjout(idVariante);
    } catch (erreur) {
      console.warn('[mrv-cart] ajout impossible', erreur);
    } finally {
      // Le re-rendu fait normalement disparaître ce bouton (le produit est au panier,
      // donc il sort de l'upsell). On le débloque quand même : si le re-rendu n'a pas
      // lieu, un bouton resté en aria-busy serait définitivement inutilisable.
      bouton.removeAttribute('aria-busy');
    }
  }

  /** Défile d'une carte, dans le sens demandé. */
  function defiler(cadre, sens) {
    const piste = cadre.querySelector(SELECTEUR_PISTE);
    if (!piste) return;
    const carte = piste.querySelector('.mrv-upsell__carte');
    const pas = carte ? carte.getBoundingClientRect().width + 10 : piste.clientWidth;
    piste.scrollBy({ left: pas * sens, behavior: 'smooth' });

    // L'écouteur `scroll` suffit en usage normal, mais il ne se déclenche pas quand
    // l'onglet est en arrière-plan. On repasse une fois le défilement doux terminé pour
    // que l'état des flèches ne dépende pas de cet événement.
    setTimeout(() => majFleches(cadre), 500);
  }

  /** Grise les flèches aux extrémités, et les masque s'il n'y a qu'une carte. */
  function majFleches(cadre) {
    const piste = cadre.querySelector(SELECTEUR_PISTE);
    const prec = cadre.querySelector('[data-mrv-upsell-prec]');
    const suiv = cadre.querySelector('[data-mrv-upsell-suiv]');
    if (!piste || !prec || !suiv) return;

    const debordement = piste.scrollWidth - piste.clientWidth > 4;

    // On n'écrit que si la valeur change : masquer une flèche modifie la largeur de la
    // piste, donc redéclenche le ResizeObserver. Sans ce garde-fou, on repart en boucle.
    const poser = (element, propriete, valeur) => {
      if (element[propriete] !== valeur) element[propriete] = valeur;
    };

    poser(prec, 'hidden', !debordement);
    poser(suiv, 'hidden', !debordement);
    if (!debordement) return;

    poser(prec, 'disabled', piste.scrollLeft <= 2);
    poser(suiv, 'disabled', piste.scrollLeft >= piste.scrollWidth - piste.clientWidth - 2);
  }

  document.addEventListener('click', (evenement) => {
    const ajout = evenement.target.closest('[data-mrv-upsell-ajouter]');
    if (ajout) {
      evenement.preventDefault();
      ajouterAuPanier(ajout);
      return;
    }

    const prec = evenement.target.closest('[data-mrv-upsell-prec]');
    if (prec) {
      defiler(prec.closest('.mrv-upsell__cadre'), -1);
      return;
    }

    const suiv = evenement.target.closest('[data-mrv-upsell-suiv]');
    if (suiv) {
      defiler(suiv.closest('.mrv-upsell__cadre'), 1);
    }
  });

  document.addEventListener(
    'scroll',
    (evenement) => {
      const piste = evenement.target;
      if (piste instanceof Element && piste.matches(SELECTEUR_PISTE)) {
        majFleches(piste.closest('.mrv-upsell__cadre'));
      }
    },
    true
  );

  /**
   * Deux observateurs, parce qu'un seul ne suffit pas — mesuré sur la boutique :
   *
   * - ResizeObserver sur la piste. Tant que le tiroir est fermé, la piste mesure 0 et
   *   `majFleches` conclut « pas de débordement » et masque les flèches. L'ouverture
   *   d'un <dialog> ne change qu'un ATTRIBUT, donc un MutationObserver en childList ne
   *   repasse jamais et les flèches restaient masquées alors qu'il y avait 3 cartes.
   *   Le ResizeObserver, lui, se déclenche à l'instant où la piste prend sa largeur.
   *   Il couvre aussi la rotation de l'écran et le redimensionnement de la fenêtre.
   *
   * - MutationObserver sur le document, pour rattacher le ResizeObserver aux pistes
   *   recréées à chaque re-rendu du tiroir par Horizon.
   */
  const surTaille = new ResizeObserver((entrees) => {
    entrees.forEach((entree) => majFleches(entree.target.closest('.mrv-upsell__cadre')));
  });

  const resynchroniser = () => {
    surTaille.disconnect();
    document.querySelectorAll(SELECTEUR_PISTE).forEach((piste) => surTaille.observe(piste));
    document.querySelectorAll('.mrv-upsell__cadre').forEach(majFleches);
  };

  const observateur = new MutationObserver(resynchroniser);

  const demarrer = () => {
    resynchroniser();
    observateur.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }

  /**
   * Redirection vers /cart apres un ajout.
   *
   * Avec `cart_type: page`, Horizon ajoute en AJAX et LAISSE la cliente sur la fiche
   * produit : seule la pastille de l'en-tete bouge, rien ne lui dit qu'elle peut payer.
   * Erreur deja payee sur Nyvora le 02/10 — "page" ne veut pas dire "redirige vers la
   * page". On redirige donc nous-memes.
   *
   * Piege mesure la-bas : `shopify:cart:lines-update` porte `action` et `context`
   * DIRECTEMENT sur l'objet evenement. `evenement.detail.action` est toujours undefined,
   * et un test dessus ne se declenche jamais.
   *
   * Les pages a offre Moon Bundle n'ont pas besoin de ce code : l'app intercepte le
   * submit et navigue elle-meme. Ce listener couvre les produits SANS offre.
   */
  document.addEventListener('shopify:cart:lines-update', (evenement) => {
    if (ajoutInterne) return;
    if (evenement.action !== 'add') return;

    // Deja sur le panier : le theme re-rend la section, recharger serait une regression.
    const chemin = window.location.pathname;
    if (chemin === '/cart' || chemin.endsWith('/cart')) return;

    window.location.assign(`${racine()}cart`);
  });
})();
